import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

const ZacksManifesto = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Zack's Manifesto | PhantomPathVPN</title>
        <meta name="description" content="Zack's Manifesto: The Ghost Steps Out. PhantomPathVPN's philosophy on privacy and connectivity." />
      </Helmet>

      <div className="founder-manifesto-page relative min-h-screen bg-[#050b14] text-gray-300 font-sans flex flex-col">
        {/* Dynamic Dark Grid Background */}
        <div className="fixed inset-0 z-0 pointer-events-none">
          <div className="absolute inset-0 bg-[#050b14]" />
          {/*
            Background Image Position Adjustment:
            Repositioned to 'center 20%' to crop out the top beam and reveal
            the planes in the lower section behind the text content.
          */}
          <div
            className="absolute inset-0 opacity-40 grayscale"
            style={{
              backgroundImage: "url('https://api.phantompathvpn.com/manifesto-bg.png')",
              backgroundSize: 'cover',
              backgroundPosition: 'center 20%',
              backgroundRepeat: 'no-repeat'
            }}
          />
          <div className="founder-manifesto-grid absolute inset-0 bg-[linear-gradient(rgba(0,255,200,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,200,0.08)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse:80%_80%_at_50%_50%,#000_60%,transparent_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0a0a0a]/50 to-[#0a0a0a]" />
        </div>

        {/* Content Wrapper */}
        <div className="relative z-10 w-full flex-grow flex flex-col pt-16 pb-24">

          {/* Profile Image - Moved to very top of hierarchy */}
          <div className="w-full flex justify-center px-4 mt-8 mb-6">
            <div className="founder-portrait-frame p-1 bg-[#0a0a0a] rounded-none border border-[#00ffc8] shadow-[0_0_15px_rgba(0,255,200,0.4)] backdrop-blur-sm transition-transform duration-500 hover:scale-105 hover:shadow-[0_0_25px_rgba(0,255,200,0.6)] flex items-center justify-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[#00ffc8]/10 animate-pulse pointer-events-none" />
              <img
                src="https://api.phantompathvpn.com/portrait.jpg"
                alt="Zack - Portrait photo with platinum blonde hair, dark clothing, professional headshot style"
                className="h-28 w-28 md:h-36 md:w-36 object-cover rounded-none relative z-10"
              />
            </div>
          </div>

          <div className="container mx-auto max-w-3xl px-4">
            <h1
              className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-[#00ffc8] text-center w-fit mx-auto leading-tight mt-2 mb-10 uppercase font-mono"
              style={{
                fontWeight: 400,
                letterSpacing: '0.05em',
                textShadow: '0 0 10px rgba(0, 255, 200, 0.5), 0 0 20px rgba(0, 255, 200, 0.3)'
              }}
            >
              ZACK'S MANIFESTO
            </h1>

            <div className="founder-manifesto-copy space-y-6 leading-relaxed text-white text-sm md:text-base text-left bg-[#0a0a0a]/60 backdrop-blur-md p-6 sm:p-8 rounded-2xl border border-white/5 shadow-2xl relative">
              <p>
                My name is Zack, the founder of PhantomPath.
              </p>
              <p>
                Before PhantomPath, I spent ten years working with paint booths, spray guns and repairing cars.
              </p>
              <p>
                Painting cars is unforgiving work. It taught me to notice details other people might miss. Precision matters. A finish can look right from a distance and still expose every mistake when examined closely.
              </p>
              <p>
                Away from the spray booth, Muay Thai became an important part of my life.
              </p>
              <p>
                Most people see fighting and software as completely separate worlds.
              </p>
              <p>
                I do not.
              </p>
              <p>
                This is where they collide.
              </p>
              <p>
                Muay Thai taught me how to stay composed under pressure. Every hour in the gym demanded discipline, and every hour building PhantomPath demanded exactly the same.
              </p>
              <p>
                Fighting gives you immediate feedback. If your timing is wrong, you know. If your defence is weak, it gets exposed. There is no presentation to hide behind.
              </p>
              <p>
                One taught me precision.
              </p>
              <p>
                The other taught me resilience.
              </p>
              <p>
                That mindset never left me.
              </p>
              <p>
                Over time, my attention shifted from cars to something happening across the digital world.
              </p>
              <p>
                People were being forced into permanent relationships for services they might only need temporarily.
              </p>
              <p>
                Another monthly VPN subscription.
              </p>
              <p>
                Another mandatory account.
              </p>
              <p>
                Another company collecting information before providing a service supposedly designed to protect the person using it.
              </p>
              <p>
                I wanted to build the opposite.
              </p>
              <p>
                Choose what you need.
              </p>
              <p>
                Pay for the required period.
              </p>
              <p>
                Receive access and use it without another monthly commitment.
              </p>
              <p>
                That idea became PhantomPath.
              </p>
              <p>
                I stepped away from the spray booth and started building PhantomPath.
              </p>
              <p>
                PhantomPath now provides three core services:
              </p>
              <p>
                <strong>Pay as you go VPN access.</strong>
              </p>
              <p>
                <strong>Travel eSIM data.</strong>
              </p>
              <p>
                <strong>UK and USA virtual phone numbers with calling and messaging.</strong>
              </p>
              <p>
                This is why I call PhantomPath a flexible platform for privacy and connectivity.
              </p>
              <p>
                The connection between Muay Thai and software became clear while I was building PhantomPath.
              </p>
              <p>
                They both expose weaknesses immediately.
              </p>
              <p>
                There is no room for excuses in either world.
              </p>
              <p>
                For months, I worked behind the PhantomPath name while I built the platform. Now it's live, and I'm putting my own name behind it.
              </p>
              <p>
                I'm Zack. I built PhantomPath.
              </p>
              <p>
                And this is only the beginning.
              </p>
              <p className="mt-4 font-sans tracking-normal">
                <strong className="text-base md:text-lg font-semibold text-white">Zack</strong><br />
                <span className="text-[10px] md:text-xs uppercase tracking-[0.18em] text-gray-400">Founder</span><br />
                <span className="text-xs md:text-sm font-medium tracking-[0.06em] text-[#00ffc8]">PhantomPathVPN</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ZacksManifesto;
